"use client";

import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState } from "react";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Non-zero staleTime is load-bearing under streaming SSR: with the
        // default of 0, every query refetches the instant it hydrates on the
        // client, immediately after the server already rendered it.
        staleTime: 60 * 1000,
        // The video poll loop drives itself with refetchInterval. Refetching
        // on focus/mount on top of that fires redundant Server Action POSTs,
        // and each one is a real OpenRouter poll.
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        // A failed poll is retried by the next interval tick anyway; internal
        // retries just stack requests on top of the interval.
        retry: 1,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Always a fresh client per request — a module-scope singleton on the
    // server would share one cache across every request.
    return makeQueryClient();
  }
  // In the browser, reuse one client so a Suspense-triggered re-render during
  // initial hydration doesn't throw away an in-flight cache.
  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  // useState (not useMemo, not module scope) so React guarantees the client
  // survives re-renders and is never shared across server requests.
  const [queryClient] = useState(getQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV !== "production" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}
